import { createSlice } from "@reduxjs/toolkit";
import { ActionStatus } from "../../../../common/constants/action-status";
import { getShopCart } from "../thunks/get-shop-cart.thunk";
import { ShopCartState } from "../interfaces/shop-cart.interface";
import { defaultApiState } from "../../../../common/constants/api-constants";
import { getLoggedInCartQuoteIdMagento } from "../thunks/get-loggedin-cart.thunk";
import { getGuestCartTokenMagento } from "../thunks/get-guest-cart-token";
import { mergeGuestCart } from "../thunks/merge-guest-cart.thunk";
import { mergeShareCart } from "../thunks/merge-share-cart.thunk";
import { addToCart } from "../thunks/add-to-cart.thunk";
import { bulkAddToCart } from "../thunks/bulk-add-to-cart.thunk";
import { removeFromCart } from "../thunks/remove-from-cart.thunk";
import { getCustomPriceCart } from "../thunks/get-custom-price.thunk";
import { initializeCart } from "../../initialize-cart/thunk/initialize-cart.thunk";
import { validateLoggedInUserConnectLicense } from "../../initialize-cart/thunk/validate-user-connect-license.thunk";
import { mapInitializeResponseToShopCart } from "../mapping/initialize-cart-response-to-shop-cart.mapping";

export const shopCartStateInitialState: ShopCartState = {
  getShopCart: defaultApiState,
  bulkAddToCart: defaultApiState,
  mergeGuestCart: defaultApiState,
  mergeSharedCart: defaultApiState,
  getCustomCart: defaultApiState,
  getGuestCartTokenMagento: defaultApiState, // Get Cart token for Magento
  createLoggedInCartQuoteIdMagento: defaultApiState,
  initializeCartMagentoResponse: defaultApiState,
  validateLoggedInUserConnectLicenseResponse: defaultApiState,
  heInstructorInvalidProductsRemoved: false,
};


export const shopCartSlice = createSlice({
    name:"shopCart",
    initialState: shopCartStateInitialState,
    reducers:{
      updateGetCartApiStatus(state, action) {
        state.getShopCart.status = action.payload;
      },
      unsetGuestCartToken(state) {
        state.getGuestCartTokenMagento.data = undefined;
        state.getGuestCartTokenMagento.status = ActionStatus.failed;
      },
      updateShopCartData(state, action) {
        state.getShopCart.data = action.payload;
      },
      setHeInstructorInvalidProductsRemoved(state, action) {
        state.heInstructorInvalidProductsRemoved = action.payload;
      },
    },
    extraReducers(builder) {
      builder
      .addCase(getShopCart.pending, (state) => {
        state.getShopCart.status = ActionStatus.loading;
      })
      .addCase(getShopCart.fulfilled, (state, action) => {
        state.getShopCart.status = ActionStatus.succeeded;
        state.getShopCart.data = action.payload;
      })
      .addCase(getShopCart.rejected, (state, action) => {
        state.getShopCart.status = ActionStatus.failed;
        state.getShopCart.error = action.payload;
      })
      .addCase(mergeGuestCart.pending, (state) => {
        state.mergeGuestCart.status = ActionStatus.loading
      })
      .addCase(mergeGuestCart.fulfilled, (state, action) => {
        state.mergeGuestCart.status = ActionStatus.succeeded
        state.mergeGuestCart.data = action.payload;
        state.getGuestCartTokenMagento.data = undefined;
      })
      .addCase(mergeGuestCart.rejected, (state, action) => {
        state.mergeGuestCart.status = ActionStatus.failed
        state.mergeGuestCart.error = action.payload;
        state.getGuestCartTokenMagento.data = undefined;
      })
      .addCase(mergeShareCart.pending, (state) => {
        state.mergeSharedCart.status = ActionStatus.loading
      })
      .addCase(mergeShareCart.fulfilled, (state, action) => {
        state.mergeSharedCart.status = ActionStatus.succeeded
        state.mergeSharedCart.data = action.payload;
      })
      .addCase(mergeShareCart.rejected, (state, action) => {
        state.mergeSharedCart.status = ActionStatus.failed
        state.mergeSharedCart.error = action.payload;
      })
      .addCase(addToCart.pending, (state) => {
        state.getShopCart.status = ActionStatus.loading;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.getShopCart.status = ActionStatus.succeeded;
        state.getShopCart.data = action.payload;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.getShopCart.status = ActionStatus.failed;
        state.getShopCart.error = action.payload
      })
      .addCase(bulkAddToCart.pending, (state) => {
        state.bulkAddToCart.status = ActionStatus.loading;
      })
      .addCase(bulkAddToCart.fulfilled, (state, action) => {
        state.bulkAddToCart.status = ActionStatus.succeeded;
        state.bulkAddToCart.data = action.payload;
      })
      .addCase(bulkAddToCart.rejected, (state, action) => {
        state.bulkAddToCart.status = ActionStatus.failed;
        state.bulkAddToCart.error = action.payload
      })
      .addCase(removeFromCart.pending, (state) => {
        state.getShopCart.status = ActionStatus.loading;
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.getShopCart.status = ActionStatus.succeeded;
        state.getShopCart.data = action.payload;
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.getShopCart.status = ActionStatus.failed;
        state.getShopCart.error = action.payload;
      })
      .addCase(getCustomPriceCart.pending, (state) => {
        state.getShopCart.status = ActionStatus.loading;
      })
      .addCase(getCustomPriceCart.fulfilled, (state, action) => {
        // If the custom price cart is successful, we want to update the normal getShopCart data
        state.getShopCart.status = ActionStatus.succeeded;
        state.getShopCart.data = action.payload;
      })
      .addCase(getCustomPriceCart.rejected, (state, action) => {
        // If the custom price cart is failed, we do NOT want to override the getShopCart status.
        // This way we can allow the user to proceed with the normal shop cart data.
        state.getCustomCart.status = ActionStatus.failed;
        state.getCustomCart.error = action.payload;
      })
      .addCase(getGuestCartTokenMagento.pending, (state) => {
        state.getGuestCartTokenMagento.status = ActionStatus.loading;
      })
      .addCase(getGuestCartTokenMagento.fulfilled, (state, action) => {
        state.getGuestCartTokenMagento.status = ActionStatus.succeeded;
        state.getGuestCartTokenMagento.data = action.payload;
      })
      .addCase(getGuestCartTokenMagento.rejected, (state, action) => {
        state.getGuestCartTokenMagento.status = ActionStatus.failed;
        state.getGuestCartTokenMagento.error = action.payload;
      })
      .addCase(getLoggedInCartQuoteIdMagento.pending, (state) => {
        state.createLoggedInCartQuoteIdMagento.status = ActionStatus.loading;
      })
      .addCase(getLoggedInCartQuoteIdMagento.fulfilled, (state, action) => {
        state.createLoggedInCartQuoteIdMagento.status = ActionStatus.succeeded;
        state.createLoggedInCartQuoteIdMagento.data = action.payload;
      })
      .addCase(getLoggedInCartQuoteIdMagento.rejected, (state, action) => {
        state.createLoggedInCartQuoteIdMagento.status = ActionStatus.failed;
        state.createLoggedInCartQuoteIdMagento.error = action.payload;
      })
      .addCase(initializeCart.pending, (state) => {
        state.initializeCartMagentoResponse.status = ActionStatus.loading;
      })
      .addCase(initializeCart.fulfilled, (state, action) => {
        state.initializeCartMagentoResponse.status = ActionStatus.succeeded;
        state.initializeCartMagentoResponse.data = action.payload;
        state.getShopCart.data = mapInitializeResponseToShopCart(action.payload);
      })
      .addCase(initializeCart.rejected, (state, action) => {
        state.initializeCartMagentoResponse.status = ActionStatus.failed;
        state.initializeCartMagentoResponse.error = action.payload;
      })
      .addCase(validateLoggedInUserConnectLicense.pending, (state) => {
        state.validateLoggedInUserConnectLicenseResponse.status = ActionStatus.loading;
      })
      .addCase(validateLoggedInUserConnectLicense.fulfilled, (state, action) => {
        state.validateLoggedInUserConnectLicenseResponse.status = ActionStatus.succeeded;
        state.validateLoggedInUserConnectLicenseResponse.data = action.payload;
      })
      .addCase(validateLoggedInUserConnectLicense.rejected, (state, action) => {
        state.validateLoggedInUserConnectLicenseResponse.status = ActionStatus.failed;
        state.validateLoggedInUserConnectLicenseResponse.error = action.payload;
      })
    }
  });
  
  /*----- CREATE SLICE ACTIONS ------*/
  
  export const {
    updateGetCartApiStatus,
    unsetGuestCartToken,
    updateShopCartData,
    setHeInstructorInvalidProductsRemoved,
  } = shopCartSlice.actions
  
  /*----- CREATE REDUCER ------*/
  
  export const shopCartReducer = shopCartSlice.reducer;